import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import PromoCodeActions from '../../actions/PromoCodeActions';
import FormattedHTMLMessage from '../general/FormattedHTMLMessage';
import Order from '../../records/Order';
import PromoCode from '../../records/PromoCode';
import t from '../../lib/I18n';
import FeatureFlagStore from '../../stores/FeatureFlagStore';

/**
 * <PromoDiscount /> is used for 2 features, which can be toggled with the enablePromoDiscount and enableDiscountCode feature flags
 * For more information, visit https://ontrackretail.atlassian.net/wiki/spaces/RAIL/pages/2693693441/Promotional+discounts
 */

const PromoDiscount = ({ promoCode, onChange, order }) => {
    const [discountCode, setDiscountCode] = useState('');

    const { promoCode: appliedPromoCode, validationError } = promoCode || new PromoCode();

    const enablePromoDiscount = FeatureFlagStore.get('enablePromoDiscount');
    const enablePromoDiscountValidation = FeatureFlagStore.get('enablePromoDiscountValidation');


    const validatePromoCode = async (code = discountCode) => {
        if (order.checkIfThisPromoCodeHasAlreadyApplied(code)) {
            return onChange(
                promoCode.set('validationError', t('booking.promoCode.theSame.discount.error')),
            );
        }

        if (order.hasPromoCode() && !order.checkIfThisPromotionalDiscountHasAlreadyApplied(code)) {
            return onChange(
                promoCode.set('validationError', t('booking.promoCode.one.discount.error')),
            );
        }

        if (enablePromoDiscount && enablePromoDiscountValidation) {
            const promo = await PromoCodeActions.validatePromoCode(code);
            return onChange(promo);
        } else {
            await PromoCodeActions.apply({ promoCode: code });
            const promo = (code && code.length > 20)
                ? new PromoCode({ validationError: t('booking.promoCode.char.limit.error') })
                : new PromoCode({ promoCode: code });
            return onChange(promo);
        }
    };

    useEffect(() => {
        // for the case when promo code is passed as a query parameter
        if (appliedPromoCode && !discountCode) {
            setDiscountCode(appliedPromoCode);
            validatePromoCode(appliedPromoCode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedPromoCode]);

    const removePromoCode = () => {
        setDiscountCode('');
        onChange(new PromoCode());
    };

    const onCTAClick = () => {
        if (appliedPromoCode) {
            return removePromoCode();
        }

        return validatePromoCode();
    };

    const onDiscountInputChange = ({ target }) => {
        setDiscountCode(target.value);
        onChange(promoCode.set('validationError', null));
    };

    const renderCodeInput = () => {
        const hasValidationError = validationError && discountCode;

        const discountInputValidationProps = hasValidationError
            ? { 'aria-describedby': 'promo-validation-error', 'aria-invalid': true }
            : { 'aria-label': t('booking.promoCode.input.aria') };

        if (appliedPromoCode) {
            return (
                <div className="applied-code">
                    {t('booking.promoCode.applied', {
                        promoCode: appliedPromoCode,
                    })}
                </div>
            );
        }

        return (
            <input
                className={`input-field ${hasValidationError && 'validation-error'}`}
                value={discountCode}
                onChange={onDiscountInputChange}
                placeholder={`${t('order.summary.discount_code')}`}
                {...discountInputValidationProps}
            />
        );
    };

    const renderValidationResult = () => {
        if (!discountCode) {
            return null;
        }

        if (validationError) {
            return (
                <span className="alert-warning" id="promo-validation-error" role="alert">
                    {validationError}
                </span>
            );
        }

        return (
            <div className="margin-top--sm">
                <FormattedHTMLMessage
                    className="margin-top--sm"
                    message={promoCode.getParsedTerms()}
                />
            </div>
        );
    };

    const CTAText = t(appliedPromoCode ? 'railcard.list.remove' : 'button.text.apply');
    const CTAAria = `${CTAText} ${t('order.summary.discount_code').toLowerCase()}`;

    const shortDiscountCode = (!discountCode || discountCode.length < 3);
    const isCTADisabled = !appliedPromoCode && shortDiscountCode;

    return (
        <section className="booking__section promo-section">
            <div className="promo-input-wrapper">
                {renderCodeInput()}

                <button
                    className="btn btn-default"
                    aria-label={CTAAria}
                    onClick={onCTAClick}
                    disabled={isCTADisabled}
                >
                    {CTAText}
                </button>
            </div>

            {renderValidationResult()}
        </section>
    );
};

PromoDiscount.propTypes = {
    promoCode: PropTypes.instanceOf(PromoCode).isRequired,
    onChange: PropTypes.func.isRequired,
    order: PropTypes.instanceOf(Order).isRequired,
};

export default PromoDiscount;



// WEBPACK FOOTER //
// ./src/components/search/PromoDiscount.js